import React, { useState } from 'react';

interface BeforeInstallPromptEvent extends Event {
    prompt: () => Promise<void>;
    userChoice: Promise<{ outcome: 'accepted' | 'dismissed'; platform: string }>;
}

interface InstallBannerProps {
    deferredPrompt: BeforeInstallPromptEvent | null;
    isInstallPromptAvailable: boolean;
    isStandalone: boolean;
    onInstallClick: () => Promise<void>;
}

export const InstallBanner: React.FC<InstallBannerProps> = ({
    deferredPrompt,
    isInstallPromptAvailable,
    isStandalone,
    onInstallClick,
}) => {
    const [isDismissed, setIsDismissed] = useState(() => {
        return localStorage.getItem('install-banner-dismissed') === 'true';
    });

    const handleDismiss = () => {
        localStorage.setItem('install-banner-dismissed', 'true');
        setIsDismissed(true);
    };

    // Don't show if:
    // - Already installed (standalone mode)
    // - User dismissed the banner
    // - Install prompt not available
    // - No deferred prompt
    if (isStandalone || isDismissed || !isInstallPromptAvailable || !deferredPrompt) {
        return null;
    }

    return (
        <div className="fixed top-0 left-0 right-0 z-50 animate-slideDown">
            <div className="bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600 shadow-lg">
                <div className="max-w-7xl mx-auto px-3 sm:px-4 py-2 sm:py-3">
                    <div className="flex items-center justify-between gap-2 sm:gap-4">
                        <div className="flex items-center gap-2 sm:gap-3 flex-1 min-w-0">
                            <div className="flex-shrink-0 w-8 h-8 sm:w-10 sm:h-10 bg-white rounded-lg flex items-center justify-center shadow">
                                <span className="text-xl sm:text-2xl">📱</span>
                            </div>
                            <div className="flex-1 min-w-0">
                                <h3 className="text-white font-bold text-sm sm:text-base md:text-lg leading-tight">
                                    Installez l'app !
                                </h3>
                                <p className="text-blue-100 text-[10px] sm:text-xs md:text-sm truncate">
                                    Accès rapide & hors-ligne
                                </p>
                            </div>
                        </div>

                        <div className="flex items-center gap-1 sm:gap-2 flex-shrink-0">
                            <button
                                onClick={onInstallClick}
                                className="px-3 sm:px-4 py-1.5 sm:py-2 bg-white text-blue-600 font-bold text-xs sm:text-sm rounded-lg hover:bg-blue-50 transition shadow-md hover:shadow-lg active:scale-95"
                            >
                                Installer
                            </button>
                            <button
                                onClick={handleDismiss}
                                className="p-1.5 sm:p-2 text-white hover:bg-white/20 rounded-lg transition"
                                aria-label="Fermer"
                            >
                                <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            <style>{`
        @keyframes slideDown {
          from {
            transform: translateY(-100%);
            opacity: 0;
          }
          to {
            transform: translateY(0);
            opacity: 1;
          }
        }
        .animate-slideDown {
          animation: slideDown 0.4s ease-out;
        }
      `}</style>
        </div>
    );
};
